<template>
  <div class="monri-card-form" :class="{ 'monri-card-form--flush': flush }">
    <div class="card-form-header">
      <div class="card-form-title">
        <b-icon icon="credit-card-2-front" font-scale="1.05"></b-icon>
        <span>{{ title || $t('checkout.enterCardDetails') }}</span>
      </div>
      <div class="card-brands">
        <span class="brand-badge">VISA</span>
        <span class="brand-badge">Mastercard</span>
        <span class="brand-badge">Maestro</span>
      </div>
    </div>

    <div class="monri-card-container">
      <div :id="cardElementId">
        <!-- Monri Components card form is mounted here -->
      </div>
      <div v-if="loading" class="monri-card-loading">
        <b-spinner></b-spinner>
        <span>{{ $t('checkout.loadingCard') }}</span>
      </div>
    </div>

    <div v-if="cardError" class="monri-error" role="alert">
      <b-icon icon="exclamation-circle-fill" class="mr-2"></b-icon>
      <span>{{ cardError }}</span>
    </div>

    <div v-if="noticeText" class="save-card-notice">
      <b-icon :icon="noticeIcon"></b-icon>
      <span>{{ noticeText }}</span>
    </div>

    <b-button
      block
      variant="primary"
      size="lg"
      class="pay-now-button"
      :disabled="!cardValid || processing"
      @click="onPay"
    >
      <b-spinner v-if="processing" small class="mr-2"></b-spinner>
      <template v-else>
        <b-icon icon="lock-fill" class="mr-2" font-scale="0.9"></b-icon>
        {{ payLabel }}
      </template>
      <span v-if="processing">{{ $t('checkout.processing') }}</span>
    </b-button>

    <div class="secure-footer">
      <b-icon icon="shield-lock-fill"></b-icon>
      <span>{{ $t('checkout.securePaymentNotice') }}</span>
    </div>
  </div>
</template>

<script>
let uidCounter = 0

export default {
  name: 'MonriCardForm',
  props: {
    // { clientSecret, authenticityToken, monriBaseUrl }
    session: { type: Object, required: true },
    // Monri confirmPayment transaction params (buyer profile)
    transactionParams: { type: Object, required: true },
    // Full button label, e.g. "Pay 504.00 RSD" or "Save Card"
    payLabel: { type: String, required: true },
    // Optional green notice under the form
    noticeText: { type: String, default: '' },
    noticeIcon: { type: String, default: 'check2-circle' },
    // Header title override
    title: { type: String, default: '' },
    locale: { type: String, default: 'en' },
    // Drop the outer panel chrome (border/padding/background) — for use inside a dialog.
    flush: { type: Boolean, default: false },
  },
  data() {
    return {
      uid: ++uidCounter,
      loading: true,
      cardValid: false,
      cardError: null,
      processing: false,
      monri: null,
      card: null,
      readyTimer: null,
      readyFallback: null,
    }
  },
  computed: {
    cardElementId() {
      return `monri-card-element-${this.uid}`
    },
  },
  methods: {
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load ${src}`))
        document.head.appendChild(script)
      })
    },

    async init() {
      await this.loadScript(`${this.session.monriBaseUrl}/dist/components.js`)

      // eslint-disable-next-line no-undef
      const monri = Monri(this.session.authenticityToken, { locale: this.locale })
      this.monri = monri

      const components = monri.components({ clientSecret: this.session.clientSecret })

      const style = {
        base: {
          fontSize: '16px',
          color: '#ffffff',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          fontSmoothing: 'antialiased',
          fontWeight: '500',
          letterSpacing: '0.02em',
          lineHeight: '1.4',
        },
        // White while typing, emerald once the field is valid.
        empty: { color: '#ffffff' },
        complete: { color: '#34d399' },
        invalid: { color: '#f87171' },
        label: {
          base: {
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: '0 0 8px 0',
          },
          invalid: { color: '#f87171' },
          complete: { color: 'rgba(255, 255, 255, 0.55)' },
          empty: { color: 'rgba(255, 255, 255, 0.55)' },
        },
        input: {
          base: {
            fontSize: '16px',
            color: '#ffffff',
            // Elevated translucent surface — sits above the panel instead of a black inset.
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            padding: '17px 18px',
            lineHeight: '1.4',
            boxShadow: 'none',
          },
          empty: {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          },
          complete: {
            backgroundColor: 'rgba(52, 211, 153, 0.1)',
            border: '1px solid rgba(52, 211, 153, 0.45)',
          },
          invalid: {
            backgroundColor: 'rgba(248, 113, 113, 0.08)',
            border: '1px solid rgba(248, 113, 113, 0.5)',
          },
        },
      }

      const card = components.create('card', {
        style,
        // Always tokenize the card: we run subscriptions and need the token for renewals.
        // Consent is disclosed via the notice shown next to the form.
        tokenizePan: true,
      })

      card.onChange((event) => {
        if (event.error) {
          this.cardError = event.error.message
          this.cardValid = false
        } else {
          this.cardError = null
          this.cardValid = true
        }
      })

      this.card = card

      this.$nextTick(() => {
        card.mount(this.cardElementId)
        this.waitForCardRender()
      })
    },

    // Monri has no "ready" event, so hide the loader once it injects the card iframe
    // into the container (with a safety fallback so the loader can never hang).
    waitForCardRender() {
      this.readyTimer = setInterval(() => {
        const container = document.getElementById(this.cardElementId)
        if (container && container.querySelector('iframe')) {
          this.stopWaiting()
          this.loading = false
        }
      }, 120)

      this.readyFallback = setTimeout(() => {
        this.stopWaiting()
        this.loading = false
      }, 8000)
    },

    stopWaiting() {
      if (this.readyTimer) {
        clearInterval(this.readyTimer)
        this.readyTimer = null
      }
      if (this.readyFallback) {
        clearTimeout(this.readyFallback)
        this.readyFallback = null
      }
    },

    async onPay() {
      if (!this.cardValid || this.processing || !this.monri || !this.card) return

      this.processing = true
      this.cardError = null

      try {
        const result = await this.monri.confirmPayment(this.card, this.transactionParams)

        if (result.error) {
          this.cardError = result.error.message
          this.$emit('error', result.error.message)
          this.processing = false
          return
        }

        if (result.result && result.result.status === 'approved') {
          // Leave processing = true; the parent hides/unmounts the form on success.
          this.$emit('approved', result.result)
        } else {
          const msg = (result.result && result.result.errors && result.result.errors.length)
            ? result.result.errors.join(', ')
            : this.$t('checkout.paymentDeclined')
          this.cardError = msg
          this.$emit('declined', msg)
          this.processing = false
        }
      } catch (err) {
        const msg = this.$t('checkout.paymentError')
        this.cardError = msg
        this.$emit('error', msg)
        this.processing = false
      }
    },
  },
  async mounted() {
    try {
      await this.init()
    } catch (e) {
      this.loading = false
      this.cardError = this.$t('checkout.paymentError')
      this.$emit('error', this.cardError)
    }
  },
  beforeDestroy() {
    this.stopWaiting()
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.monri-card-form {
  padding: 24px;
  border: 1px solid $dark-border2;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(102, 126, 234, 0.05) 0%, rgba(102, 126, 234, 0) 60%);
  animation: fadeIn 0.35s ease;
}

.monri-card-form--flush {
  padding: 0;
  border: none;
  border-radius: 0;
  background: none;
  animation: none;
}

.card-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .card-form-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--text-primary);

    svg {
      color: $protokol-royal-active;
    }
  }

  .card-brands {
    display: flex;
    align-items: center;
    gap: 6px;

    .brand-badge {
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.65);
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 6px;
      padding: 4px 8px;
      line-height: 1;
    }
  }
}

.monri-card-container {
  position: relative;
  min-height: 132px;
  margin-bottom: 18px;
}

.monri-card-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;

  ::v-deep .spinner-border {
    color: $protokol-royal-active;
  }
}

.monri-error {
  display: flex;
  align-items: center;
  color: #fca5a5;
  font-size: 0.9rem;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 10px;

  svg {
    flex-shrink: 0;
    color: #ef4444;
  }
}

.save-card-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.88rem;
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.22);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    color: #10b981;
  }
}

.pay-now-button {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 14px 24px;
  border-radius: 8px;
  margin-bottom: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.secure-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.82rem;
  color: var(--text-muted);
  margin: 0;

  svg {
    flex-shrink: 0;
    color: #10b981;
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
